import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Maximize2, Minimize2 } from "lucide-react";

/**
 * Side-by-side preview of all 3 landing page variants in resizable iframes.
 * Lets you scan copy + visuals across V1, V2, V3 at the same time.
 */
const VERSIONS = [
  {
    label: "V1",
    title: "Original",
    subtitle: "Discover & Support",
    path: "/v1",
    accent: "bg-tbc-gold/80",
  },
  {
    label: "V2",
    title: "Build Wealth Together",
    subtitle: "Discover, Support & Grow",
    path: "/v2",
    accent: "bg-tbc-coral/80",
  },
  {
    label: "V3",
    title: "Owner First (Live)",
    subtitle: "List Your Business & Get Discovered",
    path: "/v3",
    accent: "bg-emerald-500/80",
  },
];

export default function PreviewAllVersionsPage() {
  const [syncScroll, setSyncScroll] = useState(false);
  const [zoom, setZoom] = useState(0.55); // visual scale of each iframe column
  const [expandedIdx, setExpandedIdx] = useState(null);

  const handleScroll = (e, idx) => {
    if (!syncScroll || expandedIdx !== null) return;
    try {
      const src = e.currentTarget;
      const ratio =
        src.contentWindow.scrollY /
        (src.contentDocument.body.scrollHeight - src.contentWindow.innerHeight || 1);
      document.querySelectorAll("iframe[data-preview-frame]").forEach((f, i) => {
        if (i === idx) return;
        try {
          const target =
            ratio *
            (f.contentDocument.body.scrollHeight - f.contentWindow.innerHeight);
          f.contentWindow.scrollTo({ top: target, behavior: "auto" });
        } catch {
          /* cross-frame restrictions can fail silently */
        }
      });
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="min-h-screen bg-tbc-navy text-white flex flex-col" data-testid="preview-all">
      {/* ===== Toolbar ===== */}
      <header className="flex items-center gap-4 px-4 sm:px-6 py-3 border-b border-white/10 bg-tbc-navy/95 backdrop-blur-md sticky top-0 z-20">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/80 hover:text-tbc-gold transition-colors"
          data-testid="preview-back"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-body">Back to site</span>
        </Link>

        <div className="h-5 w-px bg-white/20 mx-1" />

        <h1 className="font-heading text-lg sm:text-xl">Side-by-Side Preview</h1>

        <div className="ml-auto flex items-center gap-3 sm:gap-5 flex-wrap">
          <label className="flex items-center gap-2 text-xs sm:text-sm font-body cursor-pointer">
            <input
              type="checkbox"
              checked={syncScroll}
              onChange={(e) => setSyncScroll(e.target.checked)}
              className="accent-tbc-gold w-4 h-4"
              data-testid="sync-scroll-toggle"
            />
            <span className="text-white/80">Sync scroll</span>
          </label>

          <div className="flex items-center gap-2 text-xs sm:text-sm">
            <span className="text-white/60">Zoom</span>
            <input
              type="range"
              min="0.3"
              max="1"
              step="0.05"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="accent-tbc-gold w-28 sm:w-36"
              data-testid="zoom-slider"
            />
            <span className="text-white/60 tabular-nums w-10">
              {Math.round(zoom * 100)}%
            </span>
          </div>

          <Link
            to="/preview-design"
            className="text-xs sm:text-sm text-white/60 hover:text-white transition-colors"
          >
            Design system
          </Link>
        </div>
      </header>

      {/* ===== Frames ===== */}
      <div className="flex-1 flex gap-3 p-3 sm:p-4 overflow-x-auto">
        {VERSIONS.map((v, idx) => {
          const isExpanded = expandedIdx === idx;
          const isHidden = expandedIdx !== null && !isExpanded;
          if (isHidden) return null;

          return (
            <div
              key={v.label}
              className={`flex flex-col rounded-2xl overflow-hidden bg-white shadow-2xl ring-1 ring-white/10 flex-shrink-0 ${
                isExpanded ? "flex-1" : ""
              }`}
              style={
                isExpanded
                  ? { width: "100%" }
                  : { width: `${Math.max(360, 1280 * zoom)}px` }
              }
              data-testid={`preview-frame-wrapper-${v.label.toLowerCase()}`}
            >
              {/* Card header */}
              <div className="flex items-center gap-3 px-4 py-3 bg-tbc-navy text-white border-b border-white/10">
                <span
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-full ${v.accent} text-tbc-navy font-heading font-bold text-sm`}
                >
                  {v.label}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-heading text-base leading-tight truncate">
                    {v.title}
                  </p>
                  <p className="text-xs text-white/60 font-body truncate">
                    {v.subtitle}
                  </p>
                </div>
                <Link
                  to={v.path}
                  className="text-xs text-tbc-gold hover:underline whitespace-nowrap"
                  data-testid={`preview-open-${v.label.toLowerCase()}`}
                >
                  Open ↗
                </Link>
                <button
                  onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                  className="text-white/70 hover:text-white transition-colors"
                  aria-label={isExpanded ? "Collapse" : "Expand"}
                  data-testid={`preview-expand-${v.label.toLowerCase()}`}
                >
                  {isExpanded ? (
                    <Minimize2 className="w-4 h-4" />
                  ) : (
                    <Maximize2 className="w-4 h-4" />
                  )}
                </button>
              </div>

              {/* Iframe stage — render at 1280px wide, then scale down */}
              <div className="relative flex-1 bg-white">
                {isExpanded ? (
                  <iframe
                    src={v.path}
                    title={`Preview ${v.label}`}
                    data-preview-frame={v.label}
                    onScroll={(e) => handleScroll(e, idx)}
                    className="w-full h-[calc(100vh-7.5rem)] border-0"
                  />
                ) : (
                  <div
                    className="origin-top-left bg-white"
                    style={{
                      width: "1280px",
                      height: `${Math.round(900 / zoom)}px`,
                      transform: `scale(${zoom})`,
                      transformOrigin: "top left",
                    }}
                  >
                    <iframe
                      src={v.path}
                      title={`Preview ${v.label}`}
                      data-preview-frame={v.label}
                      onScroll={(e) => handleScroll(e, idx)}
                      className="border-0"
                      style={{
                        width: "1280px",
                        height: `${Math.round(900 / zoom)}px`,
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <footer className="text-center text-xs text-white/40 font-body py-2 border-t border-white/5">
        Drag the zoom slider to resize · Toggle &quot;Sync scroll&quot; to scroll all 3 together ·
        Click the expand icon to view a single version full-width
      </footer>
    </div>
  );
}
